import React, { useMemo, useState } from "react";

const TYPES = ["ISTJ","ISFJ","INFJ","INTJ","ISTP","ISFP","INFP","INTP","ESTP","ESFP","ENFP","ENTP","ESTJ","ESFJ","ENFJ","ENTJ"];
const DEFAULT_MATCH = { ISTJ:"ESFP", ISFJ:"ESTP", INFJ:"ENFP", INTJ:"ENTP", ISTP:"ESFJ", ISFP:"ENFJ", INFP:"ENTJ", INTP:"ESTJ", ESTP:"ISFJ", ESFP:"ISTJ", ENFP:"INFJ", ENTP:"INTJ", ESTJ:"INTP", ESFJ:"ISTP", ENFJ:"ISFP", ENTJ:"INFP" };
const MODES = {
  love: { ko:"연애", en:"Love", base:42, same:[8,14,10,8], diff:[9,5,9,7] },
  friend: { ko:"친구", en:"Friendship", base:44, same:[9,10,9,7], diff:[8,6,8,8] },
  work: { ko:"팀워크", en:"Teamwork", base:40, same:[6,8,8,9], diff:[10,10,9,6] },
};
const AXES = [[0,"E","I"],[1,"S","N"],[2,"T","F"],[3,"J","P"]];

const copy = {
  same: {
    E:{ko:"둘 다 대화와 행동 속에서 에너지를 얻어 관계의 속도가 빠릅니다.",en:"Both gain energy through interaction and action, so momentum comes easily."},
    I:{ko:"조용한 시간과 깊은 대화를 함께 존중해 부담 없는 친밀감을 만듭니다.",en:"Both respect quiet time and depth, creating low-pressure closeness."},
    S:{ko:"구체적인 사실과 현실적인 약속을 공유해 생활의 합이 안정적입니다.",en:"Shared attention to facts and practical promises creates stability."},
    N:{ko:"아이디어와 미래 가능성을 함께 확장하며 대화가 풍부해집니다.",en:"Shared interest in ideas and possibility keeps conversation expansive."},
    T:{ko:"문제를 솔직하고 논리적으로 다뤄 해결 속도가 빠릅니다.",en:"Problems can be handled directly and logically, supporting fast resolution."},
    F:{ko:"감정과 관계의 영향을 중요하게 여겨 서로의 마음을 세심히 돌봅니다.",en:"Both value emotional impact and tend to care for the bond."},
    J:{ko:"계획과 약속의 기준이 비슷해 예측 가능한 관계를 만들기 쉽습니다.",en:"Similar planning preferences make commitments predictable."},
    P:{ko:"상황에 맞춰 유연하게 움직이며 함께 새로운 경험을 즐깁니다.",en:"Both adapt easily and enjoy spontaneous experience."},
  },
  different: {
    EI:{ko:"한 사람은 바깥 활동을, 다른 사람은 혼자 회복하는 시간을 더 원할 수 있습니다.",en:"One may want more outward activity while the other needs private recovery."},
    SN:{ko:"한 사람은 구체적 사실을, 다른 사람은 의미와 가능성을 먼저 말해 대화가 엇갈릴 수 있습니다.",en:"One leads with concrete facts while the other leads with meaning and possibility."},
    TF:{ko:"한 사람은 해결의 논리를, 다른 사람은 감정의 영향을 먼저 봐 같은 문제를 다르게 느낄 수 있습니다.",en:"One prioritizes logical resolution while the other first considers emotional impact."},
    JP:{ko:"한 사람은 미리 정하고 싶고, 다른 사람은 선택지를 열어두고 싶어 일정에서 마찰이 생길 수 있습니다.",en:"One wants decisions early while the other prefers open options, creating schedule friction."},
  },
};

const modeTips = {
  love:{ko:"상대가 사랑을 느끼는 방식과 혼자 회복하는 시간을 구체적으로 합의하세요.",en:"Agree on how each person feels loved and how much recovery time each needs."},
  friend:{ko:"만나는 빈도와 연락 기대치를 미리 말하면 서운함보다 편안함이 커집니다.",en:"Discuss contact and meeting expectations so the friendship stays comfortable."},
  work:{ko:"결정권, 마감, 피드백 방식을 시작 전에 정하면 서로의 강점이 성과로 연결됩니다.",en:"Define authority, deadlines, and feedback before starting so differences become performance."},
};

const MatchLab = ({ userType, lang }) => {
  const mine = userType.toUpperCase();
  const [other, setOther] = useState(DEFAULT_MATCH[mine] || "ENFP");
  const [mode, setMode] = useState("love");
  const report = useMemo(() => {
    const setting = MODES[mode];
    let score = setting.base;
    const harmony = [];
    const friction = [];
    AXES.forEach(([index,left,right], axisIndex) => {
      if (mine[index] === other[index]) {
        score += setting.same[axisIndex];
        harmony.push(copy.same[mine[index]][lang]);
      } else {
        score += setting.diff[axisIndex];
        const key = `${left}${right}`;
        friction.push(copy.different[key][lang]);
      }
    });
    const label = score >= 82
      ? (lang === "ko" ? "강한 시너지" : "Strong synergy")
      : score >= 74
      ? (lang === "ko" ? "좋은 균형" : "Good balance")
      : (lang === "ko" ? "발견형 매치" : "Discovery match");
    return { score: Math.min(score, 94), label, harmony, friction };
  }, [mine, other, mode, lang]);

  return (
    <section className="match-lab">
      <div className="section-eyebrow">COOLDOC MATCH LAB</div>
      <h2>{lang === "ko" ? "두 유형의 관계 탐사" : "Explore a type match"}</h2>
      <p className="match-intro">{lang === "ko" ? "상대 유형과 관계 상황을 선택하면 잘 맞는 지점과 대화가 필요한 지점을 나누어 보여줍니다." : "Choose another type and relationship context to see natural synergy and areas that need conversation."}</p>
      <div className="match-controls">
        <label><span>{lang === "ko" ? "나" : "Me"}</span><strong>{mine}</strong></label>
        <span className="match-symbol">×</span>
        <label><span>{lang === "ko" ? "상대" : "Other"}</span><select value={other} onChange={(event) => setOther(event.target.value)}>{TYPES.filter((type) => type !== mine).map((type) => <option key={type}>{type}</option>)}</select></label>
      </div>
      <div className="match-modes" role="group" aria-label="match context">
        {Object.entries(MODES).map(([key,value]) => <button key={key} className={mode === key ? "active" : ""} onClick={() => setMode(key)}>{value[lang]}</button>)}
      </div>
      <div className="match-score-card">
        <div className="match-ring" style={{ "--match-score": `${report.score * 3.6}deg` }}><strong>{report.score}</strong><span>/ 100</span></div>
        <div><span>{MODES[mode][lang]} MATCH</span><h3>{report.label}</h3><p>{mine} × {other}</p></div>
      </div>
      <div className="match-insights">
        <article><h3>{lang === "ko" ? "자연스럽게 잘 맞는 지점" : "Natural synergy"}</h3><ul>{report.harmony.length ? report.harmony.map((item,index)=><li key={index}>{item}</li>) : <li>{lang === "ko" ? "서로 다른 선호가 많아 역할을 잘 나누면 넓은 관점을 얻을 수 있습니다." : "Different preferences can create broader perspective when roles are clear."}</li>}</ul></article>
        <article><h3>{lang === "ko" ? "대화가 필요한 지점" : "Conversation points"}</h3><ul>{report.friction.length ? report.friction.map((item,index)=><li key={index}>{item}</li>) : <li>{lang === "ko" ? "선호가 비슷해 상대도 나와 같을 것이라 단정하지 않는 것이 중요합니다." : "Similar preferences do not mean identical needs; keep asking."}</li>}</ul></article>
      </div>
      <div className="match-tip"><strong>{lang === "ko" ? "COOLDOC 대화 팁" : "COOLDOC conversation tip"}</strong><p>{modeTips[mode][lang]}</p></div>
      <p className="match-note">{lang === "ko" ? "매치 점수는 MBTI 선호 차이를 대화하기 위한 참고 지표이며 관계의 성공이나 실패를 예측하지 않습니다." : "This score is a conversation aid based on preference patterns, not a prediction of relationship success."}</p>
    </section>
  );
};

export default MatchLab;
